import { useCallback, useState } from 'react';
import useHandleAppException from '../../../../../../hooks/useHandleAppException';
import LangEnum from '../../../../../../lang/LangEnum';
import snackbarStore from '../../../../../../stores/Snackbar.store';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { VideoCall } from '../../../../types/chat.types';
import chatRepository from '../../../../utils/chat.repository';
import { isChatStateProps, isVideoCallChatMessageType } from '../../../../utils/chat.utils';
import { UseVideoCallHandlerParams } from './useVideoCallHandler.types';

const useVideoCallHandler = ({
  state,
  chatsListState,
  changeSelectedChat,
}: UseVideoCallHandlerParams) => {
  const { translate } = useTranslation();

  const { handleErrorResponse } = useHandleAppException();

  const [videoCall, setVideoCall] = useState<VideoCall>();

  const handleStartVideoCall = useCallback(async () => {
    if (!state.activeChat) {
      return;
    }

    let chat = { ...state.activeChat };

    if (!chat.id) {
      const foundedChat = await chatsListState.createChatIfNotExists({
        id: chat.id,
        type: chat.type,
        title: chat.title,
        caseId: chat.caseId,
        participants: {
          brand: chat.brand,
          stores: chat.stores,
        },
      });
      if (!foundedChat) {
        return;
      }
      chat = foundedChat;

      changeSelectedChat(chat.id);
    }

    if (!isChatStateProps(chat)) {
      return;
    }

    const response = await chatRepository.createVideoCall(chat.id);

    if (!response.ok || !response.data?.ok) {
      const message = handleErrorResponse(response as any);
      snackbarStore.handleMessage({
        message: message || translate(LangEnum.CHAT_ERROR_CREATING_VIDEO_CALL),
        type: 'error',
      });
      return;
    }

    if (isVideoCallChatMessageType(response.data.message)) {
      setVideoCall({ ...response.data.message.data, chat: { id: chat.id } });
    }
  }, [chatsListState, state.activeChat, translate]);

  const handleJoinVideoCall = useCallback(async () => {
    if (!state.activeChat || !state.activeChat.id) {
      return;
    }

    const newState = state.activeChat.inProgressVideoCall
      ? { ...state.activeChat.inProgressVideoCall, chat: { id: state.activeChat.id } }
      : undefined;

    setVideoCall(newState);
  }, [state.activeChat]);

  const handleLeaveVideoCall = useCallback(async () => {
    setVideoCall(undefined);
  }, []);

  const handleDeleteVideoCall = useCallback(
    async (chatId?: string) => {
      if (!chatId) {
        return {
          ok: false,
        };
      }

      const response = await chatRepository.deleteVideoCall(chatId);

      if (!response.ok || !response.data?.ok) {
        snackbarStore.handleMessage({
          message: translate(LangEnum.CHAT_ERROR_DELETING_VIDEO_CALL),
          type: 'error',
        });
        return {
          ok: false,
        };
      }

      setVideoCall(undefined);
    },
    [state.activeChat],
  );

  return {
    videoCall,
    handleStartVideoCall,
    handleJoinVideoCall,
    handleLeaveVideoCall,
    handleDeleteVideoCall,
  };
};

export default useVideoCallHandler;
